import {
  forwardRef,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { LayoutStateHandler } from './components/LayoutStateHandler.js';
import { ResizableChartLayout } from './components/ResizableChartLayout.js';
import { ChartLayoutOrientationContext } from './context/ChartLayoutOrientation.context.js';
import { ChartLayoutRatioContext } from './context/ChartLayoutRatio.context.js';
import { ChartLayoutSizeContext } from './context/ChartLayoutSize.context.js';
import { ChartLayoutTemplateContext } from './context/ChartLayoutTemplate.context.js';
import { useAutomaticRatio } from './hooks/useAutomaticRatio.js';
import { Graph } from './slots/Graph.js';
import { Legend } from './slots/Legend.js';
import { Provider } from './slots/Provider.js';
import type { ChartLayoutProps } from './types/chart-layout.js';
import { ChartLayoutRef } from './types/chart-layout.js';
import { getChartLayoutSettings } from './utils/get-chart-layout-settings.js';
import { getLayoutGridSettings } from './utils/get-layout-grid-settings.js';
import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import { Grid } from '../../../../layouts/grid/Grid.js';
import { DataDtrumContext } from '../../context/DataDtrumContext.js';
import { useObserveContainerSize } from '../../hooks/useObserveContainerSize.js';
import type { LegendRatio } from '../../types/legend.js';

export const ChartLayoutRoot = forwardRef<
  ChartLayoutRef | null,
  PropsWithChildren<ChartLayoutProps>
>((props, forwardedRef) => {
  const {
    chartHeight,
    chartWidth,
    children,
    isEmpty = false,
    emptyState,
    errorState,
    showLoader = false,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'chart-layout',
    'data-dt-component': dataDtComponent,
    'aria-label': ariaLabel,
    role,
  } = props;
  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);
  const [containerSizes, gridContainer] = useObserveContainerSize();

  const containerAvailableArea = {
    width: containerSizes.width,
    height: containerSizes.height,
    wrapperHeight: containerSizes.wrapperHeight,
  };

  const chartLayoutRef = useRef<HTMLDivElement>(null);
  const mergedRefs = useMergeRefs<HTMLDivElement>([
    gridContainer,
    chartLayoutRef,
  ]);

  const {
    graphArea,
    legendArea,
    legendSettings,
    provider: Provider,
  } = getChartLayoutSettings(containerSizes, children, isEmpty);

  const {
    hidden,
    orientation,
    ratio,
    initialRatio,
    overrideRatio,
    onResize,
    showResizer,
    ratioBoundaries,
  } = legendSettings;

  const internalRatioRef = useRef<LegendRatio | undefined>(undefined);

  if (internalRatioRef.current === undefined && legendArea !== undefined) {
    internalRatioRef.current = initialRatio ?? ratio;
  }

  const legendRatio = internalRatioRef.current ?? ratio;

  const {
    legendCandidateSize,
    readyForAutoRatio,
    legendRef,
    refreshAutoRatio,
  } = useAutomaticRatio(orientation, legendRatio, containerAvailableArea);

  const { layoutWidth, layoutHeight, templateAreas, gridTemplate } =
    getLayoutGridSettings(
      chartWidth,
      chartHeight,
      containerSizes,
      !hidden,
      showResizer,
    );

  useImperativeHandle(
    forwardedRef,
    (): ChartLayoutRef => ({
      element: chartLayoutRef?.current,
      refreshLegendSize: refreshAutoRatio,
    }),
  );

  return (
    <ChartLayoutTemplateContext.Provider value={{ graphArea, legendArea }}>
      <ChartLayoutOrientationContext.Provider value={{ orientation }}>
        <ChartLayoutRatioContext.Provider
          value={{
            legendRef,
            ratio: legendRatio,
            ratioBoundaries,
            overrideRatio,
            readyForAutoRatio,
            legendCandidateSize,
          }}
        >
          <ChartLayoutSizeContext.Provider value={containerAvailableArea}>
            <DataDtrumContext.Provider
              value={{ dataDtrumAllow, dataDtrumMask }}
            >
              <Grid
                {...behavioralTrackingProps}
                data-testid={dataTestId}
                data-dtrum-mask={dataDtrumMask}
                data-dtrum-allow={dataDtrumAllow}
                data-dt-component={dataDtComponent}
                className={className}
                width={layoutWidth}
                height={layoutHeight}
                ref={mergedRefs}
                gridTemplateColumns={gridTemplate}
                gridTemplateRows={gridTemplate}
                gridTemplateAreas={templateAreas}
                gap={0}
                aria-label={ariaLabel}
                role={role}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  ...style,
                }}
              >
                <LayoutStateHandler
                  empty={isEmpty}
                  height={layoutHeight}
                  emptyState={emptyState}
                  errorState={errorState}
                  showLoader={showLoader}
                >
                  <Provider>
                    <ResizableChartLayout
                      onResize={onResize}
                      showResizer={showResizer}
                      showLegend={!hidden}
                    />
                  </Provider>
                </LayoutStateHandler>
              </Grid>
            </DataDtrumContext.Provider>
          </ChartLayoutSizeContext.Provider>
        </ChartLayoutRatioContext.Provider>
      </ChartLayoutOrientationContext.Provider>
    </ChartLayoutTemplateContext.Provider>
  );
});

(
  ChartLayoutRoot as typeof ChartLayoutRoot & { displayName: string }
).displayName = 'ChartLayoutRoot';

/**
 * Slot component for Chart Layout
 * @internal
 */
export const ChartLayout = Object.assign(ChartLayoutRoot, {
  Graph,
  Legend,
  Provider,
});
